import { Userpilot } from "userpilot";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLockKeyhole,
  faTriangleExclamation,
  faCircleXmark,
  faCircleExclamation,
} from "@fortawesome/sharp-regular-svg-icons";

import { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import styles from "./appealsremainingquickview.module.scss";
import Loader from "@/components/shared/Loader";
import { gql } from "@/__generated__";
import { useProviderHasFacilities } from "@/hooks/useProviderHasFacilities.ts";
import { useIsEnterpriseProvider } from "@/hooks/useIsEnterpriseProvider.ts";

const formatDate = (isoString: string) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

export const GET_PROVIDER_PURCHASE_INFORMATION = gql(`
  query ProviderPurchaseInfo {
    provider {
      providerId
      providerName
      freeTrialEndTimestamp
      createdTimestamp
      users {
        providerUserId
        email
        createdTimestamp
        firstName
        lastName
        jobTitle
      }
      sponsoredAppeals {
        totalRemaining
      }
      purchases {
        appealQuantity
      }
    }
  }
`);

export function AppealsRemainingQuickView() {
  const { user } = useAuthenticator();
  const providerHasFacilities = useProviderHasFacilities();
  const isEnterpriseProvider = useIsEnterpriseProvider();
  const [purchaseMade, setPurchaseMade] = useState(false);
  const [creditsRemaining, setCreditsRemaining] = useState(0);
  const {
    loading: queryLoading,
    error: queryError,
    data,
  } = useQuery(GET_PROVIDER_PURCHASE_INFORMATION, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (data) {
      if (!data.provider) {
        throw new Error("Provider not found.");
      }

      const matchingUser = data.provider.users.find(
        (providerUser) => providerUser.providerUserId === user.userId,
      );

      if (data.provider.purchases && data.provider.purchases.length > 0) {
        setPurchaseMade(true);
        setCreditsRemaining(
          data.provider.sponsoredAppeals?.totalRemaining ?? 0,
        );
      } else {
        setPurchaseMade(false);
        setCreditsRemaining(0);
      }
      Userpilot.identify(user.userId, {
        first_name: matchingUser?.firstName,
        last_name: matchingUser?.lastName,
        email: matchingUser?.email,
        created_timestamp: matchingUser?.createdTimestamp,
        job_title: matchingUser?.jobTitle,
        company: {
          id: data.provider.providerId,
          name: data.provider.providerName,
          created_at: data.provider.createdTimestamp,
        },
        provider_free_trial_end_timestamp: data.provider.freeTrialEndTimestamp,
        formatted_provider_free_trial_end_timestamp: data.provider
          .freeTrialEndTimestamp
          ? formatDate(data.provider.freeTrialEndTimestamp)
          : undefined,
      });
    }
  }, [data, user.userId]);

  if (queryError) {
    return (
      <div className={styles.navbarAppealsRemaining}>
        {queryError && (
          <div>
            <FontAwesomeIcon
              icon={faCircleExclamation}
              className={styles.queryErrorIcon}
            />
            {"Error loading appeals remaining..."}
          </div>
        )}
      </div>
    );
  }

  if (providerHasFacilities || isEnterpriseProvider) {
    return null;
  }

  if (queryLoading) {
    return (
      <div className={styles.navbarAppealsRemaining}>
        {queryLoading && (
          <Loader color="loader_almost_black" width="36" height="36" />
        )}
      </div>
    );
  }

  return (
    <div className={styles.navbarAppealsRemaining}>
      {!purchaseMade && (
        <>
          <div className={styles.navbarNeverPurchasedAppealsIconWrapper}>
            <FontAwesomeIcon icon={faLockKeyhole} />
          </div>
          <div className={styles.navbarNeverPurchasedAppealsTitle}>
            {"Sponsor appeals"}
          </div>
          <div className={styles.navbarNeverPurchasedAppealsBody}>
            {"Purchase packs and your patients appeal for free."}
          </div>
          <Link
            to="/sponsor-appeals"
            className={styles.navbarAppealsRemainingButton}
          >
            {"Sponsor appeals"}
          </Link>
        </>
      )}
      {purchaseMade && creditsRemaining > 3 && (
        <>
          <div className={styles.navbarAppealsRemainingCount}>
            {creditsRemaining}
          </div>
          <div className={styles.navbarAppealsRemainingBody}>
            {"appeals remaining"}
          </div>
          <Link
            to="/sponsor-appeals?displayPurchaseModal=true"
            className={styles.navbarAppealsRemainingButton}
          >
            {"Sponsor appeals"}
          </Link>
        </>
      )}
      {purchaseMade && creditsRemaining > 0 && creditsRemaining <= 3 && (
        <>
          <div className={styles.navbarAppealsRemainingCountWarning}>
            {creditsRemaining}
          </div>
          <div className={styles.navbarAppealsRemainingBodyWarning}>
            {`appeal${creditsRemaining === 1 ? "" : "s"} remaining`}
          </div>
          <div className={styles.navbarAppealsRemainingAlmostOutWarning}>
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className={styles.navbarAppealsRemainingAlmostOutWarningIcon}
            />
            {"You're almost out"}
          </div>
          <Link
            to="/sponsor-appeals?displayPurchaseModal=true"
            className={styles.navbarAppealsRemainingButton}
          >
            {"Sponsor appeals"}
          </Link>
        </>
      )}
      {purchaseMade && creditsRemaining === 0 && (
        <>
          <div className={styles.navbarAppealsRemainingNoneLeftCount}>
            {"0"}
          </div>
          <div className={styles.navbarAppealsRemainingNoneLeftBody}>
            {"appeals remaining"}
          </div>
          <div className={styles.navbarAppealsRemainingNoneLeftWarning}>
            <FontAwesomeIcon
              icon={faCircleXmark}
              className={styles.navbarAppealsRemainingNoneLeftWarningIcon}
            />
            {"Patients pay for appeals"}
          </div>
          <Link
            to="/sponsor-appeals?displayPurchaseModal=true"
            className={styles.navbarAppealsRemainingButton}
          >
            {"Sponsor appeals"}
          </Link>
        </>
      )}
    </div>
  );
}
